import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, TrendingUp, Shield, CheckCircle } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Guides = () => {
  const guideLinks = [
    {
      id: 1,
      title: 'Comment Choisir un Site de Paris Fiable',
      slug: 'choisir-site-paris-fiable',
      description: 'Apprenez à identifier les plateformes agréées et sécurisées pour vos paris en ligne.',
    },
    {
      id: 2,
      title: 'Comment Utiliser les Bonus de Bienvenue',
      slug: 'utiliser-bonus-bienvenue',
      description: 'Maximisez vos gains dès le départ grâce à une stratégie optimale d\'utilisation des bonus.',
    },
    {
      id: 3,
      title: 'Comment Parier sur le Football',
      slug: 'parier-football',
      description: 'Maîtrisez les techniques et stratégies pour réussir vos paris sur le football français et international.',
    },
    {
      id: 4,
      title: 'Comment Parier sur le Tennis',
      slug: 'parier-tennis',
      description: 'Découvrez les spécificités des paris tennis et les opportunités les plus rentables.',
    },
    {
      id: 5,
      title: 'Comment Gérer son Bankroll',
      slug: 'gerer-bankroll',
      description: 'Protégez votre capital et optimisez vos mises grâce à une gestion financière intelligente.',
    },
    {
      id: 6,
      title: 'Comment Lire les Cotes',
      slug: 'lire-cotes',
      description: 'Comprenez le système de cotes français et apprenez à identifier les meilleures valeurs.',
    },
    {
      id: 7,
      title: 'Comment Retirer ses Gains',
      slug: 'retirer-gains',
      description: 'Tout savoir sur les méthodes de retrait, les délais et les conditions pour récupérer vos gains rapidement.',
    },
    {
      id: 8,
      title: 'Les Types de Paris Disponibles',
      slug: 'types-paris-disponibles',
      description: 'Un guide complet sur tous les types de paris : simple, combiné, système et bien plus encore.',
    },
    {
      id: 9,
      title: 'Sécurité et Protection des Joueurs',
      slug: 'securite-protection-joueurs',
      description: 'Vos droits, les outils de protection et comment jouer en toute sécurité sur les sites agréés.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Link>

        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-600 rounded-full mb-6">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Guides Complets sur les Paris en Ligne
          </h1>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
            Tout ce que vous devez savoir pour parier intelligemment et en toute sécurité en France
          </p>
        </div>

        {/* About our guides section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Comment Nous Rédigeons Nos Guides
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p>
              Chaque guide que vous trouverez sur Lyon Picks est le fruit d'une analyse approfondie et d'une expertise 
              accumulée au fil des années dans l'univers des paris en ligne français. Nous ne nous contentons pas de compiler 
              des informations générales : nous testons, comparons et analysons chaque aspect pour vous offrir des conseils 
              concrets et actionnables.
            </p>
            <p>
              Notre méthodologie repose sur trois piliers fondamentaux : la transparence, l'objectivité et la praticité. 
              Nous examinons chaque sujet sous tous les angles, en nous basant sur les réglementations en vigueur de l'Autorité 
              Nationale des Jeux (ANJ), les retours d'expérience des parieurs, et notre propre analyse des plateformes agréées.
            </p>
            <p>
              Contrairement à de nombreux sites qui se contentent de lister des fonctionnalités, nous creusons en profondeur. 
              Quand nous parlons de bonus, nous analysons les conditions de mise réelles, pas seulement les montants affichés. 
              Quand nous expliquons les stratégies de paris, nous vous donnons des exemples concrets basés sur des situations réelles. 
              Quand nous comparons les sites, nous testons réellement les processus d'inscription, de dépôt et de retrait.
            </p>
            <p>
              Chaque guide est régulièrement mis à jour pour refléter les dernières évolutions du marché. Les bonus changent, 
              les conditions évoluent, les nouvelles fonctionnalités apparaissent : nous suivons ces changements de près pour 
              vous garantir des informations toujours à jour. Nous ne recommandons jamais un site ou une stratégie sans l'avoir 
              personnellement testée et validée selon nos critères stricts de qualité et de sécurité.
            </p>
            <p>
              Notre objectif n'est pas de vous vendre un service, mais de vous donner les outils nécessaires pour prendre des 
              décisions éclairées. Nous croyons fermement qu'un parieur bien informé est un parieur qui gagne plus et qui joue 
              de manière plus responsable. C'est pourquoi nous investissons autant de temps dans la recherche, la vérification 
              et la rédaction de contenus détaillés qui font vraiment la différence.
            </p>
          </div>
        </div>

        {/* Charts section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-12 h-12 opacity-80" />
              <span className="text-4xl font-bold">87%</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Taux de Satisfaction</h3>
            <p className="text-purple-100 text-sm">
              Des parieurs qui suivent nos guides déclarent améliorer leurs résultats
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <Shield className="w-12 h-12 opacity-80" />
              <span className="text-4xl font-bold">100%</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Sites Vérifiés</h3>
            <p className="text-blue-100 text-sm">
              Tous les sites recommandés sont agréés par l'ANJ et testés par notre équipe
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-xl p-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="w-12 h-12 opacity-80" />
              <span className="text-4xl font-bold">24/7</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Mise à Jour</h3>
            <p className="text-green-100 text-sm">
              Nos guides sont régulièrement actualisés pour refléter les dernières tendances
            </p>
          </div>
        </div>

        {/* Guide links grid */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Nos Guides Détaillés
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guideLinks.map((guide, index) => (
              <Link
                key={guide.id}
                to={`/guides/${guide.slug}`}
                data-discover="true"
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all hover:scale-105 border-l-4 border-purple-600"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-2xl font-bold text-purple-600">0{index + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {guide.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {guide.description}
                </p>
                <div className="mt-4 text-purple-600 font-semibold">
                  Lire le guide →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Guides;
